import React from 'react';
import { motion } from 'framer-motion';
import whatsappIcon from '@/assets/whatsapp.png';
import { useModal } from '@/contexts';
import { useI18n } from '@/i18n';

interface WhatsAppButtonProps {
  phoneNumber?: string;
  message?: string;
}

const WhatsAppButton: React.FC<WhatsAppButtonProps> = ({
  phoneNumber = '601112049396',
  message,
}) => {
  const { t } = useI18n();
  const {
    showLoginModal,
    showAIMatchModal,
    showTutorRegistration,
    showParentRegistration,
    showMusicTutorRegistration,
    showMusicParentRegistration,
    showCloseWarning,
  } = useModal();

  const isAnyModalOpen =
    showLoginModal ||
    showAIMatchModal ||
    showTutorRegistration ||
    showParentRegistration ||
    showMusicTutorRegistration ||
    showMusicParentRegistration ||
    showCloseWarning;
  const resolvedMessage = message ?? t('whatsapp.defaultMessage');

  if (isAnyModalOpen) return null;

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(resolvedMessage);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const RING_COUNT = 3;
  const RING_DURATION = 2.4;

  return (
    <div
      className="fixed right-6 z-[999] flex items-center justify-center w-14 h-14 transition-[bottom] duration-200 ease-out"
      style={{ bottom: 'calc(1.5rem + var(--floating-bottom-offset, 0px))' }}
    >
      {Array.from({ length: RING_COUNT }).map((_, i) => (
        <motion.span
          key={i}
          className="absolute inset-0 rounded-full bg-green-400 pointer-events-none"
          animate={{
            scale: [1, 1.5, 2.2],
            opacity: [0, 0.45, 0],
          }}
          transition={{
            duration: RING_DURATION,
            repeat: Infinity,
            delay: (i * RING_DURATION) / RING_COUNT,
            ease: 'easeOut',
            times: [0, 0.35, 1],
          }}
        />
      ))}
      <motion.button
        onClick={handleWhatsAppClick}
        className="relative z-10 rounded-full shadow-lg"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label={t('whatsapp.ariaLabel')}
        title={t('whatsapp.title')}
      >
        <img src={whatsappIcon} alt="WhatsApp" className="w-14 h-14" />
      </motion.button>
    </div>
  );
};

export default WhatsAppButton;
